import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { Add, Description } from "@mui/icons-material";
import { useDocuments } from "@/api/hooks/documents";
import { DOCUMENT_TYPE_LABELS } from "@/api/labels";
import type { DocumentEntityType } from "@/api/types";
import { formatDate } from "@/utils/format";

interface Props {
  entityType: DocumentEntityType;
  entityId: string;
}

export default function DocumentsSection({ entityType, entityId }: Props) {
  const { data, isLoading, error } = useDocuments({
    entity_type: entityType,
    entity_id: entityId,
  });

  const uploadHref =
    `/clinops/documents/upload?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Documents
          </Typography>
          <Link to={uploadHref} style={{ textDecoration: "none" }}>
            <Button size="small" startIcon={<Add />}>
              Upload
            </Button>
          </Link>
        </Box>

        {isLoading && <CircularProgress size={20} />}
        {error && <Alert severity="error">{error.message}</Alert>}

        <Stack divider={<Divider flexItem />} spacing={1}>
          {(data ?? []).map((d) => (
            <Box
              key={d.id}
              sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}
            >
              <Description color="action" />
              <Box sx={{ flex: 1 }}>
                <Link to={`/clinops/documents/${d.id}`} style={{ textDecoration: "none" }}>
                  <Typography variant="body2" sx={{ color: "primary.main" }}>
                    {d.title}
                  </Typography>
                </Link>
                <Typography variant="caption" color="text.secondary">
                  {DOCUMENT_TYPE_LABELS[d.document_type]}
                  {d.current_version &&
                    ` · v${d.current_version.version_number} · ${formatDate(d.current_version.uploaded_at)}`}
                </Typography>
              </Box>
              {d.deleted_at && (
                <Chip size="small" label="Deleted" color="error" variant="outlined" />
              )}
            </Box>
          ))}
          {(data ?? []).length === 0 && !isLoading && (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No documents attached yet.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
